import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import { getContentFromLink, getSeriesInput } from "src/services/simulator-flight";
import { getFileName, getFileType, isNewSimulationVersion, sortData } from "src/utils/common";
import { SIMULATION_TYPE, FILE_TYPE } from "src/constant/common";
import { alertState } from "src/recoil/atoms/global";
import { useMutation } from "src/Hooks/useReactQuery";
import { getErrorDetails } from "src/Hooks/useErrorDetails";
import useSeriesDataConverter from "./useSeriesDataConverter";

/**
 * Custom hook for handling series detail operations
 * Manages the fetching and processing of series data for both 3DOF and 6DOF simulations
 */
const useSeriesDetailHandler = (versionOptions) => {
  const { t } = useTranslation(["flight-simulator", "common"]);
  const showAlert = useSetRecoilState(alertState);
  const { convertDataSeries } = useSeriesDataConverter();

  const { mutate: getSeriesData } = useMutation(
    {
      mutationKey: (id) => `getSeries${id}`,
      mutationFn: (id) => getSeriesInput(id),
    },
    true
  );

  /**
   * Processes 3DOF simulation data by fetching and combining various file contents
   * @param {Object} simulationData - The simulation data object
   * @param {Array} data - Raw series data array
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  const process3DOFData = useCallback(
    async (simulationData, data, onSuccess, onError) => {
      try {
        const json = [];
        let countError = 0;
        const { id } = simulationData;

        const isNewVersion = isNewSimulationVersion(simulationData.version_name);

        await Promise.all(
          [...data].map(async (i) => {
            try {
              const aeroFilesPromises =
                i?.aero_files?.length && isNewVersion
                  ? i.aero_files.map((aeroFile) =>
                      getFileType(aeroFile?.aero_name) === FILE_TYPE.CSV && aeroFile?.aero_link
                        ? getContentFromLink(aeroFile.aero_link)
                        : Promise.resolve({ data: null })
                    )
                  : [];

              const [
                serieResponse,
                aero1Response,
                aero2Response,
                windModelResponse,
                thrustProfileResponse,
                jsonFileUpdatedResponse,
                ...aeroFilesResponses
              ] = await Promise.all([
                getContentFromLink(i?.link),
                getFileType(i?.aero1_name) === FILE_TYPE.CSV && i?.aero1_link
                  ? getContentFromLink(i?.aero1_link)
                  : Promise.resolve({ data: null }),
                getFileType(i?.aero2_name) === FILE_TYPE.CSV && i?.aero2_link
                  ? getContentFromLink(i?.aero2_link)
                  : Promise.resolve({ data: null }),
                i?.wind_model_link && getContentFromLink(i?.wind_model_link),
                i?.thrust_profile_link && getContentFromLink(i?.thrust_profile_link),
                i?.updated_link && getContentFromLink(i?.updated_link),
                ...aeroFilesPromises,
              ]);

              const aeroFilesContent =
                i?.aero_files?.length && isNewVersion
                  ? i.aero_files.map((aeroFile, index) => ({
                      ...aeroFile,
                      content: aeroFilesResponses[index]?.data || null,
                    }))
                  : [
                      ...(aero1Response.data ? [{ aero_name: i?.aero1_name, content: aero1Response.data }] : []),
                      ...(aero2Response.data ? [{ aero_name: i?.aero2_name, content: aero2Response.data }] : []),
                    ];

              json.push({
                ...i,
                param_filename: getFileName(i?.serie_file_name),
                jsonObject: serieResponse.data,
                aero1Content: aero1Response.data,
                aero2Content: aero2Response.data,
                windModelContent: windModelResponse?.data,
                thrustProfileContent: thrustProfileResponse?.data,
                jsonUpdateContent: jsonFileUpdatedResponse?.data,
                aeroFilesContent: aeroFilesContent,
              });
            } catch (error) {
              countError += 1;
              showAlert({
                status: true,
                message: t("file_error"),
                type: "danger",
              });
            }
          })
        );

        if (countError === data.length) {
          onError(id);
          return;
        }

        const seriesData = convertDataSeries(json, isNewVersion);
        onSuccess(id, {
          data: seriesData.dataSeries,
          columns: seriesData.columnSeries,
          loading: false,
          error: false,
        });
      } catch (error) {
        onError(simulationData.id);
      }
    },
    [versionOptions, convertDataSeries, showAlert, t]
  );

  /**
   * Processes 6DOF simulation data by fetching various configuration files
   * @param {Object} simulationData - The simulation data object
   * @param {Object} serieData - The series data object
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  const process6DOFData = useCallback(
    async (simulationData, serieData, onSuccess, onError) => {
      try {
        const filesContent = [];
        let fileResponseContent = "";
        const fileList = serieData?.params || serieData?.inputs;

        await Promise.all(
          [...fileList].map(async (item) => {
            try {
              const [
                jsonResponse,
                massAndShapeResponse,
                windModelResponse,
                targetAttitudeRateAscentResponse,
                targetAttitudeRateDescentResponse,
                targetAttitudeAscentResponse,
                targetAttitudeDescentResponse,
                jsonFileUpdatedResponse,
              ] = await Promise.all([
                item?.link && getContentFromLink(item?.link),
                item?.mass_shape_link && getContentFromLink(item?.mass_shape_link),
                item?.wind_model_link && getContentFromLink(item?.wind_model_link),
                item?.target_attitude_rate_ascent_link && getContentFromLink(item?.target_attitude_rate_ascent_link),
                item?.target_attitude_rate_descent_link && getContentFromLink(item?.target_attitude_rate_descent_link),
                item?.target_attitude_ascent_link && getContentFromLink(item?.target_attitude_ascent_link),
                item?.target_attitude_descent_link && getContentFromLink(item?.target_attitude_descent_link),
                item?.updated_link && getContentFromLink(item?.updated_link),
              ]);

              filesContent.push({
                ...item,
                param_filename: getFileName(item?.serie_file_name),
                jsonObject: jsonResponse?.data,
                fileContent: jsonResponse?.data,
                massAndShapeObject: massAndShapeResponse?.data,
                windModelContent: windModelResponse?.data,
                targetAttitudeRateAscentContent: targetAttitudeRateAscentResponse?.data,
                targetAttitudeRateDescentContent: targetAttitudeRateDescentResponse?.data,
                targetAttitudeAscentContent: targetAttitudeAscentResponse?.data,
                targetAttitudeDescentContent: targetAttitudeDescentResponse?.data,
                jsonUpdateContent: jsonFileUpdatedResponse?.data,
                errorList: item?.errors?.length ? getErrorDetails(item.errors, item.basename, t) : [],
              });
            } catch (error) {
              showAlert({
                status: true,
                message: t("file_error"),
                type: "danger",
              });
            }
          })
        );

        if (serieData?.response) {
          await getContentFromLink(serieData?.response?.link)
            .then((res) => {
              fileResponseContent = res.data;
            })
            .catch((error) => {
              showAlert({
                status: true,
                message: t("file_error"),
                type: "danger",
              });
            });
        }

        // filesContent is built via Promise.all + push, so its order depends on fetch resolution time
        // and is unstable across reopens. Sort it the same way the 3DOF flow does (see convertDataSeries):
        // by param_filename first, then by serie_name, so the displayed order is deterministic.
        const sortedFilesContent = sortData(sortData(filesContent, "param_filename"), "serie_name");

        onSuccess(simulationData.id, {
          data: sortedFilesContent,
          fileResponse: fileResponseContent,
          fileResponseLink: serieData?.response?.link,
          fileResponseName: serieData?.response?.name,
          loading: false,
          error: false,
        });
      } catch (error) {
        onError(simulationData.id);
      }
    },
    [showAlert, t]
  );

  /**
   * Main function to handle series detail fetching based on simulation type
   * @param {Object} simulationData - The simulation data object
   * @param {Function} onSuccess - Success callback for updating series data
   * @param {Function} onError - Error callback for handling failures
   */
  const fetchSeriesDetail = useCallback(
    (simulationData, onSuccess, onError) => {
      const { id, type } = simulationData;

      getSeriesData(id, {
        onSuccess: async (data) => {
          if (type === SIMULATION_TYPE["6DOF"] || type === SIMULATION_TYPE["6DOF_MCS"]) {
            await process6DOFData(simulationData, data, onSuccess, onError);
          } else {
            await process3DOFData(simulationData, data, onSuccess, onError);
          }
        },
        onError: () => onError(id),
      });
    },
    [getSeriesData, process3DOFData, process6DOFData]
  );

  return {
    fetchSeriesDetail,
  };
};

export default useSeriesDetailHandler;
